import * as util from "../../utils/strings";
import * as time from "../../utils/time";
import * as entries from "../../log/entries/get-entries";
import { tagObj, getTagObj } from "../../log/tags/class-tag";

// Goals
// ======================================================
export function generateGoalAttributes(line) {
  const goalAttributes = {};

  // GOAL NAME
  goalAttributes.name = String(line.match(window.regExes.frontMatterGoals)).replace("!", "");

  // GOAL ID
  goalAttributes.ID = `${goalAttributes.name}_${Math.floor(Math.random() * 898 + 100)}`;

  // GOAL TARGETS
  // reads first number, sets it as daily target
  // checks for flag, set it as the target for that flag if there is one
  // goes to next number, repeat. if no other flag, discard.

  // 1 to 3 numbers not followed by a date (more numbers. works somehow)
  let targets = line.match(/\s\d{1,3}(?!\d)/g) || ["0"];
  targets = targets.map((target) => {
    return target.replace(" ", "");
  });

  // TARGET FLAGS
  // three flag types:
  // 1. daily
  // 2. weekdays
  // 3. weekends
  // daily is the default.
  // weekdays and/or weekends can be added in the frontmatter
  // so check for that now:

  let flags = [];
  if (line.match(/\s[a-z]{1,10}/)) {
    flags = line.match(/(daily|weekdays|weekends)/g) || [];
  }
  flags = flags.map((flag) => {
    return flag.replace(" ", "");
  });

  // remove extra targets to fit to flags
  if (flags.length > 1) {
    targets = targets.slice(0, flags.length);
  }

  for (let x = 0; x <= flags.length; x++) {
    if (flags[x] == "weekdays") {
      goalAttributes.weekdays = +targets[x];
    } else if (flags[x] == "weekends") {
      goalAttributes.weekends = +targets[x];
    } else if (flags == "") {
      goalAttributes.daily = +targets[0];
    }
  }

  // DATES
  let dates = [];
  let date_start = "";
  let date_end = "";

  dates = line.match(window.regExes.date);
  if (dates) {
    goalAttributes.date_start = time.dateFormatterYYYYMMDDtoJS(dates[0]);
    if (dates.length > 1) {
      goalAttributes.date_end = time.dateFormatterYYYYMMDDtoJS(dates[1]);
    }
  }

  // DESCRIPTION
  let description = String(line.match(window.regExes.comment)) || ""; // anything between "..."
  goalAttributes.description = util.stripQuotes(description);

  // Generate the final Goal Object
  window.fm.goals[goalAttributes.ID] = new Goal(goalAttributes);

  // Make a tag object for the goal
  if (!getTagObj(goalAttributes.name)) {
    window.fm.tags.push(new tagObj(goalAttributes.name));
  }
}

class Goal {
  constructor({ name, ID, daily, weekdays, weekends, date_start, date_end, description }) {
    // console.log('--------')
    // console.log(this.name)
    let days_this_month = "";

    let target_daily = daily;
    let target_week = 0;
    let target_month = 0;
    let target_weekdays = 0;
    let target_weekends = 0;

    //if weekdays, calculate num*5 per week, week*4 per month
    if (weekdays) {
      target_weekdays = weekdays * 5;
      target_week += target_weekdays;
    }
    if (weekends) {
      target_weekends = weekends * 2;
      target_week += target_weekends;
    }

    if (target_week === 0) {
      target_week = target_daily * 7;
    }
    if (target_month === 0) {
      target_month = target_week * 4;
    }

    if (date_start) {
      date_start = time.dateNoTime(date_start);
    }
    if (date_end) {
      date_end = time.dateNoTime(date_end);
    }

    // Goal basic info
    this.tag = name;
    this.name = name;
    this.ID = ID;
    this.type = "goal";
    this.date_start = date_start;
    this.date_end = date_end;
    this.description = description || "";

    this.streak_dates_total = undefined;

    // Targets
    this.target_daily = daily;
    this.target_weekdays = weekdays;
    this.target_weekends = weekends;
    this.target_week = target_week;
    this.target_month = target_month;

    // Streaks
    this.streak_data = undefined;
    this.streak_data_test = [2, 0, 1, 0, 4, 4, 2, 1, 2, 0, 1, 2, 2, 2, 2, 1, 3, 3, 3];
    this.streak_dates_total = time.daysBetweenTwoDates(this.date_start, this.date_end) || undefined;
    this.streak_current = ""; //  4 (days)
    this.streak_longest = ""; // 19 (days)

    // Practice
    this.practice_this_period = undefined;
    this.practice_data_eachday = this.practice_data;
  }

  // This gets called once at the start of the program, calculating all the necessary data for every goal.
  // ======================================================
  update_goal_data() {
    // Is Goal active?
    this.isActive = this.isGoalActive();

    // Make a list all entries since goal start
    this.entries_since_goal_start = this.getEntriesSinceGoalStart();
    this.entries_between_start_end = this.getEntriesBetweenStartEnd();

    // Target Data
    this.target_data = this.calcTargetData();
    this.target_total = this.target_data.reduce((x, y) => {
      return x + y;
    }, 0);

    // Streak Data
    this.streak_practiced_dates =
      this.date_start && this.date_end ? this.entries_between_start_end : this.entries_since_goal_start;

    this.streak_data = this.calcStreakData();
    this.streak_dates = this.getStreakDates();

    // Practice Data
    this.practice_data = this.calcPracticeData();
    this.practiced_today = this.practice_data.reverse()[this.practice_data.length - 1];
    this.practiced_total = this.practice_data.reduce((x, y) => {
      return x + y;
    }, 0);

    this.practice_average = this.practiced_total / this.practice_data.length;
    this.practiced_shortest = Math.min(
      ...this.practice_data.filter((data) => {
        return data != 0;
      }),
    );
    this.practiced_longest = Math.max(...this.practice_data);
  }

  // Goal Period functions
  getTargetForThisDay = function (date) {
    // receive a JS day object

    // check if we are weekday or weekend
    let day_type = time.isWeekday(date);

    // check if theres a defined target for it
    if (day_type == 1) {
      return this.target_weekdays || this.target_daily;
    } else if (day_type == 0) {
      return this.target_weekends || this.target_daily;
    }
  };

  getTargetForThisPeriod = function () {
    // Calculate and return the target for a given period

    // rewrite this so it can be broken into smaller export functions
    // and also so that it can receive the query object and use that to pass dates into its sub export functions

    // returns which target is the relevant one according to what `timespan` we are looking at
    // (trget_daily, target_weekdays, target_monthly, etc)

    let target;

    // DAY
    if (time.getQueryTimeSpan() == "day") {
      let queryDate = new Date(Object.keys(window.viewQuery.query));

      // check if its a weekday or weekend

      if (time.isWeekday(queryDate)) {
        // weekdays
        if (this.target_weekdays) {
          target = this.target_weekdays;
        } else {
          target = this.target_daily;
        }
      } else {
        // weekends
        if (this.target_weekends) {
          target = this.target_weekends;
          return target;
        } else if (this.target_daily) {
          target = this.target_daily;
        } else {
          return;
        }
      }

      // WEEK OR MONTH PARTIALLY WITH START OR END DATE
    } else if (
      (time.getQueryTimeSpan() == "week" || time.getQueryTimeSpan() == "month") &&
      (this.date_start || this.date_end)
    ) {
      // get the first and last date in the query
      let query_first_date = time.dateNoTime(Object.keys(window.viewQuery.query)[0]);
      let query_last_date = time.dateNoTime(
        Object.keys(window.viewQuery.query)[Object.keys(window.viewQuery.query).length - 1],
      );

      // check if the goal start and end dates are a subset of them or not.
      // by default they are the same as the query dates...
      let goal_period_start = query_first_date;
      let goal_period_end = query_last_date;

      // ...then we will check if we use a smaller subset due to the goal having date_start or date_end
      if (this.date_start) {
        goal_period_start = this.date_start >= query_first_date ? this.date_start : query_first_date;
      }
      if (this.date_end) {
        goal_period_end = this.date_end <= query_last_date ? this.date_end : query_last_date;
      }

      // the default date_range is just the query,
      // but make it a subset if we have goal date_start or date_end
      let query_date_range = window.viewQuery.queryDates;

      if (goal_period_start == this.date_start || goal_period_end == this.date_end) {
        // Make an array of the query_dates that are all in unix time for easier comparison
        let log_query_dates_notime = window.viewQuery.queryDates.map((date) => {
          return (date = time.dateNoTime(date).getTime());
        });

        // remove extraneous dates according to the start and end dates
        let log_query_period_subset = log_query_dates_notime.slice(
          log_query_dates_notime.indexOf(goal_period_start.getTime()),
          log_query_dates_notime.indexOf(goal_period_end.getTime()) + 1,
          // +1 cos in .slice() the last date is not inclusive
        );
        query_date_range = log_query_period_subset;
      }

      // Calculate the specific Goal target according to weekend and weekday duration differences,
      // as well as the date_start and date_end within our defined range

      let period_target = 0; // the target starts at zero then we increment it
      let days_in_period = query_date_range.length;

      // add the duration of the weekday targets
      if (target_weekdays) {
        // get number of weekdays
        let number_of_weekdays = time.countWeekdaysAndWeekends(query_date_range).weekdays;
        period_target += weekdays * number_of_weekdays;
        days_in_period -= number_of_weekdays;

        // if there are none, add the duration of the daily targets
      } else if (this.target_daily) {
        period_target += this.target_daily * days_in_period;
      }
      // add the duration of the weekend targets
      if (target_weekends) {
        // get number of weekends
        let number_of_weekends = time.countWeekdaysAndWeekends(query_date_range).weekends;
        period_target += weekends * number_of_weekends;
        days_in_period -= number_of_weekends;
      }
      return period_target;

      // WEEK FULL
    } else if (time.getQueryTimeSpan() == "week") {
      target = this.target_week;

      // MONTH FULL
    } else if (time.getQueryTimeSpan() == "month") {
      target = this.target_month;
    }

    // Now that we have the target, return it
    return target;
  };

  attained = function () {
    return this.totalPracticedThisPeriod() >= this.getTargetForThisPeriod() ? true : false;
  };

  totalPracticedThisPeriod = function () {
    return window.viewQuery.querySummary.mainSummaryList[this.tag];
  };

  overshoot = function () {
    let thisPeriod = this.totalPracticedThisPeriod();
    let target = this.getTargetForThisPeriod();
    return thisPeriod > target ? `(+${time.minsToHM(thisPeriod - target)})` : "";
  };

  isInPeriod = function () {
    // Check if we are weekend or weekday, abort if its the wrong one
    // weekends
    if (!time.isWeekday(window.viewQuery.queryDateFirst)) {
      if (this.target_weekends == undefined && this.target_weekdays) {
        return false;
      } else {
        return true;
      }
      // weekdays
    } else if (this.target_weekdays == undefined && this.target_weekends) {
      return false;
    } else {
      return true;
    }
  };

  // Goal Data functions
  tagFromId = function (goalID) {
    let tag_name = goalID.match(/\w{1,}_/)[0].replace("_", "");
    let tag = getTagObj(tag_name);
    return tag;
  };

  isGoalActive = function () {
    // return true if goal is active

    if (this.date_start) {
      if (time.dateNoTime(this.date_start) > time.dateNoTime(window.viewQuery.queryDateFirst)) {
        return false;
      }
    }
    if (this.date_end) {
      if (time.dateNoTime(this.date_end) < time.dateNoTime(window.viewQuery.queryDateFirst)) {
        return false;
      }
    }

    // console.log('=== goal.name ', this.ID)
    // console.log('start date' , this.date_start)
    // console.log('end date' , this.date_end)
    // console.log('goal starts before ', time.dateNoTime(window.viewQuery.queryDateFirst))
    // console.log('goal ends after ', time.dateNoTime(window.viewQuery.queryDateFirst))

    // No aborts, so goal is active
    return true;
  };

  calcTargetData = function () {
    // for each entry
    // if its a weekday, push target_weekdays or target_daily
    // if its a weekend, push target_weekend or target_daily

    let target = this.target_daily || this.target_weekdays || this.target_weekends;
    let target_data = [];

    this.entries_since_goal_start.forEach((entry) => {
      let target = this.getTargetForThisDay(new Date(entry[0]));
      if (target == undefined) {
        target = 0;
      }
      target_data.push(target);
    });
    return target_data.reverse();
  };

  calcPracticeData = function () {
    let practice_data = [];

    this.streak_practiced_dates.forEach((entry) => {
      let data = entry[1].summary[this.tag];
      if (data == undefined) {
        data = 0;
      }
      practice_data.unshift(data);
    });
    return practice_data.reverse();
  };

  calcStreakData = function () {
    // Calculate Streak data

    // 1. Get Streak start-date
    let first_streak_date = this.date_start || this.getFirstStreakDateWithTag(this.tag);
    let last_streak_date = this.date_end || window.viewQuery.today;
    if (!first_streak_date) {
      return undefined;
    }

    // 2. Make Streak-data array
    // ====================================
    let streak_data = [];
    let streak_list = [...this.streak_practiced_dates].reverse();

    for (let x = 0; x < streak_list.length; x++) {
      let date = new Date(streak_list[x][0]);

      // Check if we are within the date range of the streaks
      if (date.getTime() >= first_streak_date.getTime()) {
        let target_for_this_day = this.getTargetForThisDay(date);
        let weekday = time.isWeekday(date);
        let summary = streak_list[x][1].summary;
        let value = summary[this.tag];

        if (value < target_for_this_day && value != 0) {
          streak_data.push(1); // partial
        } else if (value >= target_for_this_day) {
          streak_data.push(2); // full
        } else if (time.is_this_date_today(date)) {
          streak_data.push(3); // blank (today's block)
        } else if (value == undefined || value == 0) {
          if (weekday == 0) {
            streak_data.push(4); // skip
          } else {
            streak_data.push(0); // missed
          }
        }
      }
    }

    return streak_data.slice(streak_data.length - 30, streak_data.length);
  };

  getStreakDates = function () {
    // get the number of streak dates
    // loop up and for each one, print out a date into the array
    // return the array
    let dates = [this.date_start];

    let streak_dates = this.streak_dates_total || this.streak_practiced_dates.length - 1;

    for (let date = 0; date < streak_dates; date++) {
      let thedate = dates[date];
      let nextDate = new Date(dates[date]);
      nextDate.setDate(nextDate.getDate() + 1);
      dates.push(nextDate);
    }
    return dates;
  };

  // Goal Utility functions

  getTargetForThisDay = function (date) {
    // receive a JS day object

    // check if we are weekday or weekend
    let day_type = time.isWeekday(date);

    // check if theres a defined target for it
    if (day_type == 1) {
      return this.target_weekdays || this.target_daily;
    } else if (day_type == 0) {
      return this.target_weekends || this.target_daily;
    }
  };

  getFirstStreakDateWithTag = function (tag) {
    let earliest_date_with_streak;
    // starting today, go backwards through the log
    // until we find a day with no practice on this entry
    // first_streak_date = utils.getLatestDateWithTag(this.tag)

    // does yesterday have streak data? then update var
    // does yesterday's yesterday?
    // keep going until that is a no.

    for (let x = 1; x < Object.entries(window.logObj.fullLogDesc).length; x++) {
      let date = Object.entries(window.logObj.fullLogDesc)[x];
      let does_date_have_tag = false;
      let tag_list = Object.entries(date[1][1].summary);

      does_date_have_tag = tag_list.some((array) => {
        return array.includes(tag); // return true or false
      });

      if (does_date_have_tag === true) {
        earliest_date_with_streak = date[1][0];
      } else if (does_date_have_tag === false) {
        break;
      }
    }

    // if there was no match in the whole window.logObj...
    if (earliest_date_with_streak == undefined) {
      console.warn(`No first_streak_date for ${tag}`);
      return;
    }

    return (earliest_date_with_streak = time.dateNoTime(earliest_date_with_streak));
  };

  goalDatesMessage = function () {
    // return a string mentioning the hours per period, weekday/weekend, etc
    let message = "";
    if (this.date_start && this.date_end) {
      message = `${time.dateFormatterJStoYYYYMMDD(this.date_start)} - ${time.dateFormatterJStoYYYYMMDD(this.date_end)}`;
    } else if (this.date_start || this.date_end) {
      let string = this.date_start || this.date_end;
      if (string == this.date_start) {
        string = "From:";
      } else if (string == this.date_end) {
        string = "Until:";
      }
      message = `${string} ${time.dateFormatterJStoYYYYMMDD(this.date_start || this.date_end)}`;
    }
    return message;
  };

  goalFormatMessage = function () {
    let message = undefined;
    if (this.target_daily) {
      message = [`Everyday ${time.minsToHM(this.target_daily)}`];
    } else if (this.target_weekdays && this.target_weekends) {
      message = [`Weekdays ${time.minsToHM(this.target_weekdays)}`, `Weekends ${time.minsToHM(this.target_weekends)}`];
    } else if (this.target_weekdays) {
      message = [`Weekdays ${time.minsToHM(this.target_weekdays)}`];
    } else if (this.target_weekends) {
      message = [`Weekends ${time.minsToHM(this.target_weekends)}`];
    }
    return message;
  };

  getEntriesSinceGoalStart = function () {
    let start = this.date_start || this.getFirstStreakDateWithTag(this.tag);
    return entries.getEntriesFilteredByDates(Object.entries(window.logObj.fullLog), start, undefined, "from");
  };

  getEntriesBetweenStartEnd = function () {
    let start = this.date_start || this.getFirstStreakDateWithTag(this.tag);
    return entries.getEntriesFilteredByDates(Object.entries(window.logObj.fullLog), this.date_start, this.date_end);
  };
}

export function updateDataGoals() {
  Object.values(window.fm.goals).forEach((goal) => {
    goal.update_goal_data();
  });
}

export function parseGoals() {
  // return
  // add an entry for this goal into every object since the start_date
  // get each entry since the start date
  let arrayOfDaysSinceGoal_start_date = Object.keys(window.logObj.fullLogNoTime).filter((entry) => {
    return time.compareTwoDatesExcludingTime(entry, window.fm.goals["ar"].start_date);
  });

  // for each date since start_date, add a goal entry

  // sort dates ascending
  sortDatesAscending(arrayOfDaysSinceGoal_start_date);

  // add goal entry to each one, in the
  arrayOfDaysSinceGoal_start_date.forEach((entry) => {
    window.logObj.fullLogNoTime[entry].entries
      .push
      // new Entry('00:00', '00:00', '', 'ar', 1)
      // {tags: ["launchschool"], duration: 0, comment: '', start: ''}
      ();
  });
}
